import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { Alert, Button, Card, Spinner } from 'react-bootstrap';
import { toast } from 'react-toastify';
import recallsService from '../../services/recalls.service';
import Loading from '../common/Loading';
import { RECALL_STATUS_BADGES, formatStatusLabel } from '../process/processQueueUtils';
import RecallProcessor from './RecallProcessor';
const STATUS_OPTIONS = ['PENDING', 'IN_PROCESS', 'COMPLETED', 'FAILED'];

const StatusBadge = ({ status }) => {
    const badgeClass = RECALL_STATUS_BADGES[status] || 'text-bg-secondary';
    return <span className={`badge ${badgeClass}`}>{formatStatusLabel(status)}</span>;
};

const InfoRow = ({ label, children }) => (
    <div className="notifications-info-row">
        <span className="notifications-info-label">{label}</span>
        <span className="notifications-info-value">{children ?? '—'}</span>
    </div>
);

function RecallEdit() {
    const { recallId } = useParams();
    const fileInputRef = useRef(null);
    const [recall, setRecall] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isUploading, setIsUploading] = useState(false);
    const [isProcessing, setIsProcessing] = useState(false);
    const [isDragOver, setIsDragOver] = useState(false);
    const [downloadingOriginal, setDownloadingOriginal] = useState(false);

    const {
        register,
        handleSubmit,
        reset,
        formState: { errors, isSubmitting },
    } = useForm();

    const loadRecall = useCallback(() => {
        setLoading(true);
        setError(null);

        recallsService
            .getRecallRequestById(recallId)
            .then((res) => {
                setRecall(res.data);
                reset({
                    status: res.data.status,
                    adminNotes: res.data.adminNotes || '',
                });
            })
            .catch((err) => {
                console.error(err);
                setError('Failed to load recall request details.');
                setRecall(null);
            })
            .finally(() => setLoading(false));
    }, [recallId, reset]);

    useEffect(() => {
        loadRecall();
    }, [loadRecall]);

    const onSubmit = (data) => {
        recallsService
            .updateRecallRequestById(recallId, {
                id: recall.id,
                clinicId: recall.clinicId,
                status: data.status,
                adminNotes: data.adminNotes,
            })
            .then((res) => {
                setRecall(res.data);
                reset({
                    status: res.data.status,
                    adminNotes: res.data.adminNotes || '',
                });
                toast.success('Recall request updated successfully.');
            })
            .catch((err) => {
                console.error(err);
                toast.error(err.response?.data?.message || 'Failed to update recall request.');
            });
    };

    const getFileName = (filePath) => {
        if (!filePath || filePath === 'NO_PATH') return null;
        return filePath.split('/').pop();
    };

    const handleDownloadOriginal = async () => {
        if (!recall?.clinicId || !recall?.id || !recall?.filePath || recall.filePath === 'NO_PATH') return;

        setDownloadingOriginal(true);
        try {
            const response = await recallsService.downloadRecallRequestFile(recall.clinicId, recall.id);
            const fileName = getFileName(recall.filePath) || `recall-${recall.id}.csv`;
            const blob = new Blob([response.data], { type: response.headers['content-type'] || 'text/csv' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = fileName;
            link.click();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            console.error(err);
            toast.error('Failed to download original file.');
        } finally {
            setDownloadingOriginal(false);
        }
    };

    const uploadFile = async (file) => {
        if (file.type !== 'text/csv' && !file.name.toLowerCase().endsWith('.csv')) {
            toast.error('Please upload a CSV file.');
            return;
        }

        setIsUploading(true);
        try {
            const response = await recallsService.uploadRecallFileToProcess(recallId, file);
            setRecall((prev) => ({ ...prev, ...response.data }));
            toast.success('Processed file uploaded successfully.');
        } catch (err) {
            console.error(err);
            toast.error(err.response?.data?.message || 'Failed to upload processed file.');
        } finally {
            setIsUploading(false);
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    };

    const handleFileSelect = (event) => {
        const file = event.target.files?.[0];
        if (file) uploadFile(file);
    };

    const handleProcessFile = async () => {
        if (!recall?.clinicId || !recall?.id || !recall?.processedFilePath) {
            toast.error('Upload a processed CSV before starting processing.');
            return;
        }
        if (recall.status === 'IN_PROCESS') {
            toast.info('Recall file is already being processed.');
            return;
        }

        setIsProcessing(true);
        try {
            await recallsService.processRecallFile(recall.clinicId, recall.id);
            toast.success('Recall file processing started.');
            const response = await recallsService.getRecallRequestById(recallId);
            setRecall(response.data);
            reset({
                status: response.data.status,
                adminNotes: response.data.adminNotes || '',
            });
        } catch (err) {
            console.error(err);
            toast.error(err.response?.data?.message || 'Failed to start recall file processing.');
        } finally {
            setIsProcessing(false);
        }
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        if (!isUploading) setIsDragOver(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        setIsDragOver(false);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        setIsDragOver(false);
        if (isUploading) return;
        const file = e.dataTransfer.files?.[0];
        if (file) uploadFile(file);
    };

    const triggerFileInput = () => {
        if (!isUploading && fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    const originalFileName = getFileName(recall?.filePath);
    const processedFileName = getFileName(recall?.processedFilePath);

    return (
        <div className="container-fluid">
            <div className="pagetitle d-flex justify-content-between align-items-center flex-wrap gap-2">
                <div>
                    <h1 className="mb-1">
                        <i className="bi bi-repeat"></i> Edit Recall Details
                    </h1>
                    <p className="recall-page__meta mb-0">Review clinic submission and update admin status.</p>
                </div>
                <Link to="/recalls" className="btn btn-outline-secondary btn-sm">
                    <i className="bi bi-arrow-left me-1"></i>
                    Back to Recall Requests
                </Link>
            </div>

            <Card className="card-padding shadow-sm border-0">
                <Card.Body className="p-3">
                    {loading ? (
                        <Loading />
                    ) : error ? (
                        <Alert variant="warning" className="border-0 mb-0">
                            {error}
                        </Alert>
                    ) : (
                        recall && (
                            <form onSubmit={handleSubmit(onSubmit)} className="recall-edit">
                                <div className="recall-edit__header">
                                    <div>
                                        <h5 className="recall-page__title mb-1">{recall.title || 'Untitled Request'}</h5>
                                        <div className="recall-edit__summary">
                                            <StatusBadge status={recall.status} />
                                            {recall.clinicName && (
                                                <span className="text-muted small">{recall.clinicName}</span>
                                            )}
                                            {recall.clinicId && (
                                                <Link
                                                    to={`/clinics/${recall.clinicId}`}
                                                    className="small"
                                                    target="_blank"
                                                    rel="noreferrer"
                                                >
                                                    View clinic
                                                </Link>
                                            )}
                                        </div>
                                    </div>
                                    <div className="text-muted small">
                                        Created {recall.createdAt || '—'}
                                        {recall.modifiedAt ? ` · Updated ${recall.modifiedAt}` : ''}
                                    </div>
                                </div>

                                <div className="row g-3">
                                    <div className="col-lg-8 recall-edit__column">
                                        <div className="notifications-panel mb-3">
                                            <div className="notifications-panel__header">
                                                <i className="bi bi-info-circle"></i>
                                                Request Information
                                            </div>
                                            <div className="notifications-panel__body">
                                                <div className="row g-3">
                                                    <div className="col-md-6">
                                                        <InfoRow label="Request ID">
                                                            <span className="font-monospace small">{recall.id}</span>
                                                        </InfoRow>
                                                    </div>
                                                    <div className="col-md-6">
                                                        <InfoRow label="Clinic ID">
                                                            <span className="font-monospace small">{recall.clinicId}</span>
                                                        </InfoRow>
                                                    </div>
                                                    <div className="col-md-6">
                                                        <InfoRow label="Title">{recall.title}</InfoRow>
                                                    </div>
                                                    <div className="col-md-6">
                                                        <InfoRow label="Current Status">
                                                            <StatusBadge status={recall.status} />
                                                        </InfoRow>
                                                    </div>
                                                    <div className="col-12">
                                                        <InfoRow label="Notes">
                                                            <pre className="recall-edit__notes small mb-0">
                                                                {recall.notes || '—'}
                                                            </pre>
                                                        </InfoRow>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="notifications-panel recall-edit__file-panel">
                                            <div className="notifications-panel__header">
                                                <i className="bi bi-file-earmark-arrow-up"></i>
                                                File Management
                                            </div>
                                            <div className="notifications-panel__body d-grid gap-3">
                                                <div className="min-w-0">
                                                    <div className="text-muted small mb-2">Original clinic file</div>
                                                    {originalFileName ? (
                                                        <div className="recall-edit__file-row">
                                                            <div className="recall-edit__file-name">
                                                                <i className="bi bi-file-earmark-text text-primary"></i>
                                                                <span title={originalFileName}>{originalFileName}</span>
                                                            </div>
                                                            <Button
                                                                variant="outline-primary"
                                                                size="sm"
                                                                className="recall-edit__file-action"
                                                                onClick={handleDownloadOriginal}
                                                                disabled={downloadingOriginal}
                                                            >
                                                                {downloadingOriginal ? (
                                                                    <>
                                                                        <Spinner animation="border" size="sm" className="me-1" />
                                                                        Downloading...
                                                                    </>
                                                                ) : (
                                                                    <>
                                                                        <i className="bi bi-download me-1"></i>
                                                                        Download
                                                                    </>
                                                                )}
                                                            </Button>
                                                        </div>
                                                    ) : (
                                                        <div className="recall-edit__file-row text-muted">
                                                            No original file uploaded.
                                                        </div>
                                                    )}
                                                </div>

                                                <div className="min-w-0">
                                                    <div className="text-muted small mb-2">Processed file</div>
                                                    {processedFileName ? (
                                                        <div className="recall-edit__file-row mb-2">
                                                            <div className="recall-edit__file-name">
                                                                <i className="bi bi-file-earmark-check text-success"></i>
                                                                <span title={processedFileName}>{processedFileName}</span>
                                                            </div>
                                                            <Button
                                                                variant="success"
                                                                size="sm"
                                                                className="recall-edit__file-action"
                                                                onClick={handleProcessFile}
                                                                disabled={isProcessing || recall?.status === 'IN_PROCESS'}
                                                            >
                                                                {isProcessing || recall?.status === 'IN_PROCESS' ? (
                                                                    <>
                                                                        <Spinner animation="border" size="sm" className="me-1" />
                                                                        {recall?.status === 'IN_PROCESS' ? 'Processing...' : 'Starting...'}
                                                                    </>
                                                                ) : (
                                                                    <>
                                                                        <i className="bi bi-play-fill me-1"></i>
                                                                        Process file
                                                                    </>
                                                                )}
                                                            </Button>
                                                        </div>
                                                    ) : (
                                                        <div className="recall-edit__file-row text-muted mb-2">
                                                            No processed file uploaded yet.
                                                        </div>
                                                    )}

                                                    <input
                                                        type="file"
                                                        ref={fileInputRef}
                                                        onChange={handleFileSelect}
                                                        accept=".csv,text/csv"
                                                        className="d-none"
                                                        disabled={isUploading}
                                                    />
                                                    <div
                                                        className={`recall-edit__dropzone${
                                                            isDragOver ? ' recall-edit__dropzone--active' : ''
                                                        }${isUploading ? ' recall-edit__dropzone--disabled' : ''}`}
                                                        onClick={triggerFileInput}
                                                        onDragOver={handleDragOver}
                                                        onDragLeave={handleDragLeave}
                                                        onDrop={handleDrop}
                                                        role="button"
                                                        tabIndex={0}
                                                        onKeyDown={(e) => e.key === 'Enter' && triggerFileInput()}
                                                    >
                                                        {isUploading ? (
                                                            <>
                                                                <Spinner animation="border" size="sm" className="me-2" />
                                                                Uploading processed file...
                                                            </>
                                                        ) : (
                                                            <>
                                                                <i className="bi bi-cloud-arrow-up fs-4 text-muted d-block mb-2"></i>
                                                                <div className="small fw-medium">Upload processed CSV</div>
                                                                <div className="text-muted small">
                                                                    Drag and drop or click to browse
                                                                </div>
                                                            </>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        <RecallProcessor recallId={recallId} />
                                    </div>

                                    <div className="col-lg-4 recall-edit__column">
                                        <div className="notifications-panel">
                                            <div className="notifications-panel__header">
                                                <i className="bi bi-gear"></i>
                                                Admin Update
                                            </div>
                                            <div className="notifications-panel__body">
                                                <div className="mb-3">
                                                    <label htmlFor="recall-status" className="form-label small fw-semibold">
                                                        Status
                                                    </label>
                                                    <select
                                                        id="recall-status"
                                                        className={`form-select form-select-sm${
                                                            errors.status ? ' is-invalid' : ''
                                                        }`}
                                                        {...register('status', { required: 'Status is required' })}
                                                    >
                                                        <option value="">Select status</option>
                                                        {STATUS_OPTIONS.map((status) => (
                                                            <option key={status} value={status}>
                                                                {formatStatusLabel(status)}
                                                            </option>
                                                        ))}
                                                    </select>
                                                    {errors.status && (
                                                        <div className="invalid-feedback">{errors.status.message}</div>
                                                    )}
                                                </div>

                                                <div className="mb-0">
                                                    <label htmlFor="recall-admin-notes" className="form-label small fw-semibold">
                                                        Admin Notes
                                                    </label>
                                                    <textarea
                                                        id="recall-admin-notes"
                                                        rows={6}
                                                        className="form-control form-control-sm"
                                                        placeholder="Add internal admin notes..."
                                                        {...register('adminNotes')}
                                                    />
                                                </div>

                                                <div className="notifications-form-hint mt-3 mb-0">
                                                    <i className="bi bi-info-circle me-1"></i>
                                                    Title and clinic notes are read-only submission fields.
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="recall-edit__form-actions">
                                    <Link to="/recalls" className="btn btn-outline-secondary btn-sm">
                                        Cancel
                                    </Link>
                                    <Button type="submit" variant="primary" size="sm" disabled={isSubmitting}>
                                        {isSubmitting ? (
                                            <>
                                                <Spinner animation="border" size="sm" className="me-1" />
                                                Saving...
                                            </>
                                        ) : (
                                            <>
                                                <i className="bi bi-check-circle me-1"></i>
                                                Save Changes
                                            </>
                                        )}
                                    </Button>
                                </div>
                            </form>
                        )
                    )}
                </Card.Body>
            </Card>
        </div>
    );
}

export default RecallEdit;
